import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { IconPicker } from "@/components/shared/IconPicker";
import { showToast } from "@/components/shared/Toast";
import { Plus, ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface Session {
  _key: string;
  name: string;
  icon: string;
  description: string;
  proposed_dates: string[];
  fixed_date: string | null;
  points: number | null;
  created_at: string;
}

function formatTimeToEvent(dateStr: string): string {
  const now = new Date();
  const event = new Date(dateStr + "T00:00:00");
  const diffDays = Math.round((event.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const abs = Math.abs(diffDays);
    if (abs === 1) return "1 day ago";
    if (abs < 7) return `${abs} days ago`;
    if (abs < 14) return "1 week ago";
    return `${Math.floor(abs / 7)} weeks ago`;
  }
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays < 7) return `in ${diffDays} days`;
  if (diffDays < 14) return "in 1 week";
  if (diffDays < 30) return `in ${Math.floor(diffDays / 7)} weeks`;
  if (diffDays < 60) return "in 1 month";
  return `in ${Math.floor(diffDays / 30)} months`;
}

export function AdminSessions() {
  const { jwt, clientId } = useParams();
  const { getHeaders } = useAuth();
  const navigate = useNavigate();
  const { encryptFields, decryptList, hasKey } = useEncryption(clientId);
  const { data: rawSessions, loading, error, refetch } = useApi<Session[]>(
    `/api/admin/${jwt}/${clientId}/sessions`,
  );
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Decrypt session names/descriptions after fetch
  useEffect(() => {
    if (!rawSessions) return;
    decryptList(rawSessions as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setSessions(d as unknown as Session[]));
  }, [rawSessions, decryptList]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const handleCreate = async (values: Record<string, unknown>) => {
    // Encrypt name and description before sending
    const encrypted = await encryptFields(values as Record<string, unknown>, ["name", "description"]);
    setSubmitLoading(true);
    try {
      const res = await fetch(`/api/admin/${jwt}/${clientId}/sessions`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify(encrypted),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setShowForm(false);
      refetch();
      showToast.success("Session created!");
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  const draft = sessions?.filter((s) => !s.fixed_date) || [];
  const fixed = sessions?.filter((s) => s.fixed_date) || [];

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-heading font-bold">Sessions</h1>
        <Button onClick={() => setShowForm(true)} className="bg-app-primary hover:bg-app-primary-hover">
          <Plus className="w-4 h-4 mr-1" /> New Session
        </Button>
      </div>

      {showForm && (
        <Card className="border-t-4 border-t-app-primary">
          <CardContent className="p-3 md:p-4">
            <Formik initialValues={{ name: "", icon: "📅", description: "" }} onSubmit={handleCreate}>
              {({ values, setFieldValue }) => (
                <Form className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="space-y-1">
                      <Label>Icon</Label>
                      <IconPicker value={values.icon} onSelect={(emoji) => setFieldValue("icon", emoji)} />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label>Name</Label>
                      <Field as={Input} name="name" placeholder="Session name" autoFocus />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Description</Label>
                    <Field as={Textarea} name="description" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    <Button type="submit" className="bg-app-primary hover:bg-app-primary-hover" disabled={submitLoading}>Create</Button>
                  </div>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      )}

      {/* Draft Sessions */}
      {draft.length > 0 && (
        <div>
          <h2 className="text-body font-semibold text-app-text-muted mb-2">📋 Drafts</h2>
          <div className="space-y-2">
            {draft.map((s) => (
              <Card key={s._key} className="cursor-pointer border-l-4 border-l-app-accent hover:shadow-md hover:bg-app-surface-raised/50 transition-all py-0 gap-0" onClick={() => navigate(`/admin/${jwt}/${clientId}/sessions/${s._key}`)}>
                <CardContent className="p-2 md:p-2.5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-app-accent/10 flex items-center justify-center text-lg">{s.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-semibold">{s.name}</h3>
                    <p className="text-info text-app-text-muted">{(s.proposed_dates || []).length} dates proposed</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-app-text-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Fixed Sessions */}
      {fixed.length > 0 && (
        <div>
          <h2 className="text-body font-semibold text-app-text-muted mb-2">✅ Scheduled</h2>
          <div className="space-y-2">
            {fixed.map((s) => (
              <Card key={s._key} className="cursor-pointer border-l-4 border-l-app-success hover:shadow-md hover:bg-app-surface-raised/50 transition-all py-0 gap-0" onClick={() => navigate(`/admin/${jwt}/${clientId}/sessions/${s._key}`)}>
                <CardContent className="p-2 md:p-2.5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-app-success/10 flex items-center justify-center text-lg">{s.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-body font-semibold">{s.name}</h3>
                    <div className="flex gap-1.5 mt-0.5">
                      <span className="text-info bg-app-success/10 text-app-success px-2 py-0.5 rounded-full">{s.fixed_date}</span>
                      {s.points !== null && <span className="text-info bg-app-primary/10 text-app-primary px-2 py-0.5 rounded-full">{s.points} pts</span>}
                      <span className="text-info bg-app-surface-raised text-app-text-muted px-2 py-0.5 rounded-full border border-app-border/40">{formatTimeToEvent(s.fixed_date!)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-app-text-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {(!sessions || sessions.length === 0) && !showForm && (
        <EmptyState icon="📅" title="No sessions yet" description="Create a session to plan your next meetup!" />
      )}
    </div>
  );
}
